import { useCallback, useState } from 'react';

import { useGenerateTexts } from '@/features/campaign-texts/hooks/useGenerateTexts';
import { useGenerateSenders } from '@/features/campaign-texts/hooks/useGenerateSenders';
import { useTranslateTexts } from '@/features/campaign-texts/hooks/useTranslateTexts';

import { useBulkEditAiTextPicksSync } from './useBulkEditAiTextPicksSync';
import { runMultiCountrySenderGeneration } from '../shared/multiCountryAiSenderRun';
import { runMultiCountryTextGeneration } from '../shared/multiCountryAiTextRun';
import type { CountryAiMode, CountryGenResult } from '../shared/multiCountryBulkEdit.types';
import type { GenerationProgress } from '../shared/perDropGeneration';
export type { GenerationProgress } from '../shared/perDropGeneration';
export type { CountryAiMode, CountryGenResult } from '../shared/multiCountryBulkEdit.types';

export interface UseMultiCountryCreativeParams {
  countryKeys: string[];
  dropCountByCountry: Map<string, number>;
  globalCountryPerDrop: string[];
  onTextsGenerated?: (textsByCountry: Record<string, string[]>) => void;
  onSendersGenerated?: (sendersByCountry: Record<string, string[]>) => void;
  onTextsPerDropMergeOrder?: (batchesInMergeOrder: string[][]) => void;
  onSendersPerDropMergeOrder?: (batchesInMergeOrder: string[][]) => void;
  syncPerDropSmsTexts?: (batches: string[][]) => void;
  syncSmsTextsByCountry?: (by: Record<string, string[]>) => void;
}

export function useMultiCountryCreative(params: UseMultiCountryCreativeParams) {
  const {
    countryKeys,
    dropCountByCountry,
    globalCountryPerDrop,
    onTextsGenerated,
    onSendersGenerated,
    onTextsPerDropMergeOrder,
    onSendersPerDropMergeOrder,
    syncPerDropSmsTexts,
    syncSmsTextsByCountry,
  } = params;
  const genTexts = useGenerateTexts();
  const genSenders = useGenerateSenders();
  const translateMutation = useTranslateTexts();

  const [textMode, setTextMode] = useState<CountryAiMode>('per_drop');
  const [senderMode, setSenderMode] = useState<CountryAiMode>('same_all');
  const [resultsByCountry, setResultsByCountry] = useState<Record<string, CountryGenResult>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);

  const picks = useBulkEditAiTextPicksSync({
    countryKeys,
    globalCountryPerDrop,
    textMode,
    resultsByCountry,
    setResultsByCountry,
    genTexts,
    translateMutation,
    syncPerDropSmsTexts,
    syncSmsTextsByCountry,
  });

  const generateTextsForAllCountries = useCallback(
    async (groupId: string, amount: number, groupIdsPerDrop?: string[], translate?: boolean) => {
      setIsGenerating(true);
      try {
        await runMultiCountryTextGeneration({
          textMode,
          countryKeys,
          globalCountryPerDrop,
          groupId,
          amount,
          groupIdsPerDrop,
          translate,
          genTexts,
          setProgress,
          setResultsByCountry,
          onTextsPerDropMergeOrder,
          onTextsGenerated: textMode !== 'per_drop' ? onTextsGenerated : undefined,
        });
      } finally {
        setIsGenerating(false);
        setProgress(null);
      }
    },
    [countryKeys, globalCountryPerDrop, textMode, genTexts, onTextsGenerated, onTextsPerDropMergeOrder],
  );

  const generateSendersForAllCountries = useCallback(
    async (groupId: string, amount: number, groupIdsPerDrop?: string[]) => {
      setIsGenerating(true);
      try {
        await runMultiCountrySenderGeneration({
          senderMode,
          countryKeys,
          globalCountryPerDrop,
          groupId,
          amount,
          groupIdsPerDrop,
          genSenders,
          setProgress,
          setResultsByCountry,
          onSendersPerDropMergeOrder,
          onSendersGenerated: senderMode !== 'per_drop' ? onSendersGenerated : undefined,
        });
      } finally {
        setIsGenerating(false);
        setProgress(null);
      }
    },
    [countryKeys, globalCountryPerDrop, senderMode, genSenders, onSendersGenerated, onSendersPerDropMergeOrder],
  );

  const clearResults = useCallback(() => {
    setResultsByCountry({});
    picks.clearTextPicks();
  }, [picks]);

  const totalDropCount =
    globalCountryPerDrop.length > 0
      ? globalCountryPerDrop.length
      : countryKeys.reduce((s, ck) => s + (dropCountByCountry.get(ck) ?? 0), 0);

  return {
    textMode,
    setTextMode,
    senderMode,
    setSenderMode,
    resultsByCountry,
    textPicks: picks.textPicks,
    isGenerating,
    progress,
    generateTextsForAllCountries,
    generateSendersForAllCountries,
    clearResults,
    translateCountryTexts: picks.translateCountryTexts,
    translateMissingInBatch: picks.translateMissingInBatch,
    translatingCountryKey: picks.translatingCountryKey,
    translatingBatch: picks.translatingBatch,
    toggleAiTextPick: picks.toggleAiTextPick,
    setAllAiTextPicksForBatch: picks.setAllAiTextPicksForBatch,
    setAllAiTextPicksForCountry: picks.setAllAiTextPicksForCountry,
    regenerateUnselectedInBatch: picks.regenerateUnselectedInBatch,
    isRegeneratingTexts: genTexts.isPending,
    regenTarget: picks.regenTarget,
    countryKeys,
    dropCountByCountry,
    totalDropCount,
  };
}
